import React, { useContext, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useSelector } from 'react-redux'
import useAppView from 'Core/Hooks/useAppView'
import { FeatureFlagContext } from 'Core/FeatureFlagContext'
import { LICENSE_VIEWS_ENTRIES } from 'Core/Hooks/useLicense/constants'
import { useLicense } from 'Core/Hooks/useLicense'
import Loader from 'Components/Loader'
import { useHistory } from 'react-router'
import { enqueueNotification } from 'Utils/Helpers'
import { AppHeader, Typography } from 'procyon-ui'
import { ShieldCheck, User as UserIcon } from 'lucide-react'
import { useUser } from 'Core/Hooks/useUser'
// Leaf-component-only module — the full V2Components barrel pulls graph
// components whose feature imports would drag features into the eager bundle.
import { LoadingFeedback, PingOneLogo, PingPrivilegeLogo } from 'V2Components/ShellExports'
import axios from 'axios'
import localforage from 'localforage'
import { getHTMLDataTestID } from 'infra/utils'
import { useLayoutContext } from 'features/layout'

const useStyles = makeStyles((theme) => ({
  root: {},
  appHeaderWrapper: {
    position: 'fixed',
    top: 0,
    width: '100vw',
    zIndex: 1500,
    borderBottom: 'var(--border-width) solid var(--color-border-base)',
    backgroundColor: 'var(--color-bg-base)',
    // The profile dropdown panel is hard-coded to 200px in procyon-ui's
    // UserMainMenu — too narrow for the grouped menu (segmented toggle +
    // long view labels). The panel wrapper is a sibling of the trigger's
    // PARENT row (the trigger itself is the row's last child), so the row
    // is reached with :has(). Its inline width is beaten with !important;
    // the panel anchors to its right edge, so widening only grows it
    // leftward. (Attribute selectors use ^= because getHTMLDataTestID
    // appends a theme suffix — e.g. "...-trigger-default".)
    '& div:has(> [data-testid^="header-div-profile-dropdown-trigger"]) + div > div': {
      width: '300px !important'
    },
    // The toggle row: the menu wraps every item title in an inner flex row
    // that shrink-wraps content (the outer row is space-between), so a
    // percentage-width track inside it collapses to its content width.
    // Growing that wrapper lets the track genuinely span the row.
    '& [data-testid^="header-div-access-mode-toggle"] > div': {
      flex: '1 1 auto'
    }
  },
  logo: {
    '& img': {
      width: '125px'
    }
  }
}))

function Header() {
  const classes = useStyles()
  const history = useHistory()
  const { viewMode } = useAppView()
  const nextViewMode = viewMode === 'admin' ? 'user' : 'admin'
  const { updateContext: asyncChangeAppView, updateLicenseMode } = useContext(FeatureFlagContext)
  const { licenseMode } = useLicense()
  const { isAgentlessMode } = useUser()
  const [orgChanging, setOrgChanging] = useState(false)
  const [appViewChanging, setAppViewChanging] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const { whoAmI } = useLayoutContext()
  const user = useSelector((state) => state.user)
  const envName = whoAmI?.tenantname

  const copyID = (ID) => {
    try {
      navigator.clipboard.writeText(ID)
      enqueueNotification('Derived ID Copied!', 'info')
    } catch (error) {
      // ignore
    }
  }

  const switchViewMode = async () => {
    setAppViewChanging(true)
    await asyncChangeAppView(nextViewMode)
    setAppViewChanging(false)
  }

  const switchLicenseView = (mode) => {
    updateLicenseMode(mode)
  }

  const logoutUser = async () => {
    setLoggingOut(true)
    try {
      const hostName = window.location.host
      const updatedHost = hostName.replace('console.', '')
      const logoutURL = `https://console.login.${updatedHost}/logout`

      const response = await axios.get(logoutURL, {
        withCredentials: true
      })
      if (response && response.data) {
        // clear all the cookies
        document.cookie.split(';').forEach((cookie) => {
          const name = cookie.split('=')[0].trim()
          document.cookie = `${name}=; max-age=0; path=/;`
        })

        // clear localStorage too
        localStorage.clear()

        // Clear IndexedDB too
        if (window.indexedDB && window.indexedDB.databases) {
          try {
            const dbs = await window.indexedDB.databases()
            for (const db of dbs) {
              if (db.name) window.indexedDB.deleteDatabase(db.name)
            }
          } catch (e) {
            console.error('Failed to clear IndexedDB databases', e)
          }
        } else {
          // fallback to localforage.clear() if databases() is not supported
          try {
            await localforage.clear()
          } catch (e) {
            console.error('Failed to clear localforage', e)
          }
        }
        window.location.href = response.data
      } else {
        console.error('Logout failed. Empty or invalid response.')
      }
    } catch (error) {
      console.error('Logout error:', error)
      setLoggingOut(false)
    }
  }

  const getHeaderMenuItems = () => {
    const items = []
    const isAdmin = whoAmI ? whoAmI.isadmin : user?.role === 'admin'

    // Grouped menu — Settings / Access Mode toggle / Privilege views. The
    // procyon-ui dropdown takes JSX titles, so the toggle and the active-view
    // check are plain spans styled in place. All styling stays at the
    // dropdown's own defaults (14px, ~5px row padding) so the menu keeps the
    // original compact rhythm — only active states are styled.

    if (isAdmin) {
      // Settings — a single clickable row at the top (section header and
      // item are the same thing here; a separate header row duplicated it).
      items.push({
        action: () => history.push('/admin/settings'),
        title: 'Settings',
        dataTestId: getHTMLDataTestID('header', 'div', 'settings')
      })

      // Access mode — a segmented toggle: one pill-shaped track holding both
      // modes, the active one in a raised segment with its icon tinted.
      // Rendered as a single inert-label row whose title is the whole toggle;
      // the two segments carry their own click handlers and data-testids.
      const segment = (mode, label, Icon) => {
        const active = viewMode === mode
        return (
          <span
            key={mode}
            data-testid={getHTMLDataTestID('header', 'div', active ? `active-app-view-${mode}` : `switch-app-view-${mode}`)}
            onClick={active ? undefined : switchViewMode}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              flex: 1,
              minHeight: 34,
              padding: '5px 0',
              fontSize: 14,
              borderRadius: 9,
              cursor: active ? 'default' : 'pointer',
              color: active ? 'var(--color-primary)' : 'var(--color-font-base)',
              fontWeight: active ? 600 : 400,
              backgroundColor: active ? 'var(--color-common-bg-base)' : 'transparent',
              boxShadow: active ? '0 1px 4px rgba(0,0,0,0.25)' : undefined
            }}
          >
            <Icon size={13} />
            {label}
          </span>
        )
      }
      items.push({
        isSectionLabel: true,
        dataTestId: getHTMLDataTestID('header', 'div', 'access-mode-toggle'),
        title: (
          <span
            style={{
              display: 'inline-flex',
              width: '100%',
              margin: '4px 0 2px',
              padding: 3,
              borderRadius: 10,
              backgroundColor: 'var(--color-bg-base)',
              border: '1px solid var(--color-common-border)',
              boxSizing: 'border-box'
            }}
          >
            {segment('user', 'User', UserIcon)}
            {segment('admin', 'Admin', ShieldCheck)}
          </span>
        ),
        style: { padding: 0 }
      })

      // Privilege views — all three listed so the dropdown also shows which
      // view is active: the current one is highlighted (color + weight) and
      // inert, the other two switch. Admin surface only (the user-view
      // sidebar is not licensed).
      if (viewMode === 'admin') {
        LICENSE_VIEWS_ENTRIES.forEach(([mode, label]) => {
          const isActive = mode === licenseMode
          items.push({
            action: isActive ? undefined : () => switchLicenseView(mode),
            title: label,
            style: isActive
              ? {
                  color: 'var(--color-primary)',
                  fontWeight: 600,
                  cursor: 'default'
                }
              : undefined,
            dataTestId: getHTMLDataTestID(
              'header',
              'div',
              isActive ? `active-license-view-${mode}` : `switch-license-view-${mode}`
            )
          })
        })
      }
    }

    if (viewMode === 'user') {
      if (user?.Spec?.AzureDerivedID) {
        items.push({
          action: () => copyID(user?.Spec?.AzureDerivedID),
          title: `Copy Azure Derived ID`
        })
      }
    }

    if (isAgentlessMode) {
      items.push({
        action: logoutUser,
        title: 'Logout'
      })
    }

    return items
  }

  return (
    <div className={classes.root}>
      <div className={classes.appHeaderWrapper}>
        <AppHeader
          legacy={false}
          showNotificationBell={false}
          dropDownMenuItems={[...getHeaderMenuItems()]}
          BrandLogo={
            <div className='flex items-center justify-between gap-4'>
              <PingOneLogo className='!w-[150px]' />
              <PingPrivilegeLogo className='!w-[150px] mt-1' />
            </div>
          }
          className={classes.logo}
          userName={user?.Spec?.UserName ? user.Spec.UserName : user?.Spec?.ServerUserName}
          onClickNotificationButton={function noRefCheck() {}}
          userRole={
            <div className='flex items-center gap-1'>
              <Typography className='!text-gray-700' variant='bodySmall-regular'>
                <span className='capitalize'>{viewMode}</span>@
                <strong>{envName ?? '...'}</strong>
              </Typography>
            </div>
          }
        />
      </div>

      <LoadingFeedback
        loading={orgChanging}
        caption='Please wait'
        message='Changing Organization'
      />
      <LoadingFeedback loading={loggingOut} caption='Please wait' message='Logging Off' />
      <Loader loading={appViewChanging} />
    </div>
  )
}

export { Header }
